import axios from 'axios';
import UserProfile from '../Common/UserProfile';
import QueryString from 'query-string';
import swal from 'sweetalert';
import jwt from 'jsonwebtoken';
import LogRocket from 'logrocket';
import { API_MAX_LIMIT } from '../Common/constants';
import { nextPageFilter, shouldAutoPaginate } from './apiPagination';
import {
  forceLogout,
  getSharedRefreshPromise,
  isAccessTokenExpiredOrNearExpiry,
  isSamlAccessToken,
  isSessionEnding
} from './authSession';

function isRefreshUrl(url) {
  return url && String(url).indexOf('/AccountUsers/refresh') !== -1;
}

var PORT = '';
var isLocalhost = false;
if (window.location.hostname === 'localhost') {
  PORT = ':3000';
  isLocalhost = true;
}
export const API_URL = window.location.protocol + '//' + window.location.hostname + PORT + '/api/';

function encodeLoopbackQueryValue(value) {
  return encodeURIComponent(JSON.stringify(value));
}

/**
 * Deduplicate in-flight GETs with the same URL (e.g. DicLocal* / AccountUsers
 * requested by several components at once). Complements per-resource caches;
 * still useful under HTTP/1.1's ~6 connections per origin.
 */
const inflightGets = new Map();

function coalesceAxiosGet(axiosApi, fullPath) {
  const existing = inflightGets.get(fullPath);
  if (existing) return existing;

  const request = axiosApi.get(fullPath).finally(() => {
    if (inflightGets.get(fullPath) === request) {
      inflightGets.delete(fullPath);
    }
  });

  inflightGets.set(fullPath, request);
  return request;
}

class Api {
  constructor(props) {
    let altHearders = null;
    let altTimeout = {};
    if (props) {
      if (props.headers) {
        altHearders = props.headers;
      }

      if (props.timeout) {
        altTimeout = {
          timeout: props.timeout
        };
      }
    }
    const token = UserProfile.getToken();

    if (window.location.pathname.startsWith('/register/')) {
      let axiosApi = axios.create({});

      axiosApi.interceptors.response.use(this.handleSuccess, this.handleError);
      this.axiosApi = axiosApi;
    } else if (token != null && token !== '') {
      const realm = UserProfile.getRealm();

      let axiosApi = axios.create({
        headers: {
          Authorization: `Bearer ${token}`,
          Realm: realm,
          ...altHearders
        },
        ...altTimeout
      });

      // Proactive refresh + always use the latest cookie token.
      // Each `new Api()` snapshots Authorization at construction; without this,
      // parallel instances keep sending an expired JWT → N× TOKEN_EXPIRED before
      // the response interceptor can coalesce a single refresh.
      axiosApi.interceptors.request.use(async (config) => {
        if (isRefreshUrl(config.url)) return config;

        if (isSessionEnding()) {
          const err = new Error('Session ending');
          err.name = 'SESSION_ENDING';
          return Promise.reject(err);
        }

        const latest = UserProfile.getToken();
        if (latest) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${latest}`;
        }

        if (!isAccessTokenExpiredOrNearExpiry()) return config;

        // SAML without opaque cookie: send the request; backend renews via header.
        if (!UserProfile.getRefreshToken()) {
          if (isSamlAccessToken(latest)) return config;

          const err = new Error('The provided JWT has expired');
          err.name = 'TOKEN_EXPIRED';
          forceLogout(err);
          return Promise.reject(err);
        }

        try {
          const accessToken = await getSharedRefreshPromise();
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${accessToken}`;
          axiosApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          return config;
        } catch (refreshError) {
          forceLogout(refreshError);
          return Promise.reject(refreshError);
        }
      });

      // SAML compatibility: backend may return a renewed JWT in this header. It has to be consumed on failed responses too — the header is set before the route runs, so dropping it on a 4xx/5xx left the browser sending the expired token forever.
      const consumeRenewedToken = (response) => {
        if (!response || !response.headers || !response.headers.refresh_token) return null;

        const refreshTokenHeader = response.headers.refresh_token;
        try {
          const decoded = jwt.decode(refreshTokenHeader);
          const remainingTtl =
            decoded && decoded.exp ? Math.max(0, decoded.exp - Math.floor(Date.now() / 1000)) : 0;
          UserProfile.setToken(refreshTokenHeader, remainingTtl);
          axiosApi.defaults.headers.common['Authorization'] = `Bearer ${refreshTokenHeader}`;

          return refreshTokenHeader;
        } catch (e) {
          // ignore malformed SAML refresh header
          return null;
        }
      };

      axiosApi.interceptors.response.use(
        (response) => {
          consumeRenewedToken(response);
          return response;
        },
        async (error) => {
          const renewedToken = consumeRenewedToken(error && error.response);

          const originalRequest = error.config || {};
          const status = error.response && error.response.status;
          const isRefreshCall = isRefreshUrl(originalRequest.url);

          if (renewedToken && status === 401 && !originalRequest._retry && !isRefreshCall) {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${renewedToken}`;

            return axiosApi(originalRequest);
          }

          if (
            status === 401 &&
            !originalRequest._retry &&
            !isRefreshCall &&
            UserProfile.getRefreshToken()
          ) {
            originalRequest._retry = true;

            try {
              const accessToken = await getSharedRefreshPromise();
              axiosApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return axiosApi(originalRequest);
            } catch (refreshError) {
              forceLogout(refreshError);
              return Promise.reject(refreshError);
            }
          }

          if (status === 401 && isRefreshCall) {
            return Promise.reject(error);
          }

          return this.handleError(error);
        }
      );
      axiosApi.defaults.headers.common['locale'] = 'en-US';
      axiosApi.defaults.headers.common['locale'] = 'en-UK';
      this.axiosApi = axiosApi;

      // Prevent redirect when already in login page
    } else if (!window.location.pathname.startsWith('/login') && !isSessionEnding()) {
      forceLogout();
    }
  }

  handleSuccess(response) {
    return response;
  }

  handleError = (error) => {
    if (isSessionEnding() || (error && error.name === 'SESSION_ENDING')) {
      return Promise.reject(error);
    }

    // SERVER OFFLINE
    if (error.response === undefined) {
      swal({
        type: 'error',
        title: error.message,
        text: 'Server or Connection down. Please check your internet connection.',
        closeOnConfirm: true
      });
      // SERVER TIMEOUT
    } else if (error.code === 'ECONNABORTED') {
      swal({
        type: 'error',
        title: error.message,
        text: 'Connection Timeout',
        closeOnConfirm: true
      });
      // CUSTOM ERRORS
    } else {
      const err = error.response;
      switch (error.response.status) {
        case 400:
          console.log(error.response);
          if (sessionStorage.getItem('AUDIT_ERROS')) LogRocket.captureException(err);
          return this.handleSuccess(error.response);
        case 401:
          forceLogout(error.response && error.response.data && error.response.data.error);
          break;
        case 404:
          console.log(error.response);
          this.redirectTo(document, '/not-found');
          break;
        default: {
          const status = error.response?.status;
          // 4xx client errors: let the caller handle and show translated messages
          if (status >= 400 && status < 500) {
            if (sessionStorage.getItem('AUDIT_ERROS')) LogRocket.captureException(err);

            return this.handleSuccess(error.response);
          }
          if (isLocalhost) {
            swal({
              title: err.data?.error?.statusCode + ': ' + (err.data?.error?.name || 'Error'),
              text: err.data?.error?.message || error.message,
              type: 'error',
              closeOnConfirm: true
            });
          } else {
            console.log(error.response);
            if (sessionStorage.getItem('AUDIT_ERROS')) LogRocket.captureException(err);
            return this.handleSuccess(error.response);
          }
          break;
        }
      }
    }
    return Promise.reject(error);
  };

  redirectTo = (document, path) => {
    document.location = path;
  };

  buildFilterUrl(path, filter) {
    let fullPath = API_URL + path;

    if (filter !== null && filter !== undefined) {
      fullPath = fullPath + '?filter=' + encodeLoopbackQueryValue(filter);
    }

    return fullPath;
  }

  async fetchListPage(path, filter) {
    const fullPath = this.buildFilterUrl(path, filter);

    return coalesceAxiosGet(this.axiosApi, fullPath);
  }

  async getAllPages(path, filter = {}) {
    const baseFilter = filter && typeof filter === 'object' ? { ...filter } : {};
    const requestedLimit =
      baseFilter.limit === undefined || baseFilter.limit === null ? null : Number(baseFilter.limit);
    const maxItems =
      requestedLimit !== null && !isNaN(requestedLimit) ? requestedLimit : Number.POSITIVE_INFINITY;

    let skip = baseFilter.skip || 0;
    const all = [];
    let lastResponse = null;

    while (all.length < maxItems) {
      const pageFilter = nextPageFilter(baseFilter, skip, maxItems - all.length, API_MAX_LIMIT);

      lastResponse = await this.fetchListPage(path, pageFilter);
      const data = lastResponse && lastResponse.data;

      // Non-array payloads (unexpected for list finds) — return as-is.
      if (!Array.isArray(data)) return lastResponse;

      all.push(...data);

      if (data.length < pageFilter.limit) break;

      skip += data.length;

      // Hard safety against runaway loops.
      if (skip > 100000) break;

      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    return {
      ...(lastResponse || {}),
      status: (lastResponse && lastResponse.status) || 200,
      data: all
    };
  }

  get(path, filter, cb) {
    if (!this.axiosApi) {
      return Promise.reject(new Error('Not authenticated'));
    }

    const request = shouldAutoPaginate(path, filter)
      ? this.getAllPages(path, filter || {})
      : this.fetchListPage(path, filter);

    return request
      .then((response) => cb(response.status, response.data, this.axiosApi))
      .catch((error) => {
        console.error('Error on request API:', path, error);
      });
  }

  async getAsync(path, filter = null) {
    if (!this.axiosApi) {
      throw new Error('Not authenticated');
    }

    try {
      if (shouldAutoPaginate(path, filter)) {
        return await this.getAllPages(path, filter || {});
      }

      return await this.fetchListPage(path, filter);
    } catch (error) {
      console.error('Error on request API:', path, error);
      throw error; // Re-throw the error for the caller to handle
    }
  }

  async getJobs(path, filter) {
    if (shouldAutoPaginate(path, filter)) {
      const response = await this.getAllPages(path, filter || {});
      return response.data;
    }

    const response = await this.fetchListPage(path, filter);
    return response.data;
  }

  getParam(path, params, cb, options) {
    let fullPath = API_URL + path;
    if (params !== null && params !== undefined) {
      fullPath = fullPath + '?' + QueryString.stringify(params);
    }

    const request =
      options && options.fresh
        ? this.axiosApi.get(fullPath)
        : coalesceAxiosGet(this.axiosApi, fullPath);

    return request.then((response) => cb(response.status, response.data, this.axiosApi));
  }

  async getParamAsync(path, params) {
    let fullPath = API_URL + path;

    if (params !== null && params !== undefined) {
      fullPath = fullPath + '?' + QueryString.stringify(params);
    }

    return coalesceAxiosGet(this.axiosApi, fullPath);
  }

  getCount(path, filter, cb) {
    if (!this.axiosApi) {
      return Promise.reject(new Error('Not authenticated'));
    }
    let fullPath = API_URL + path + '/count';
    if (filter !== null && filter !== undefined) {
      fullPath = fullPath + '?where=' + encodeLoopbackQueryValue(filter);
    }
    return coalesceAxiosGet(this.axiosApi, fullPath).then((response) =>
      cb(response.status, response.data)
    );
  }

  post(path, payload, cb) {
    if (!this.axiosApi) {
      return Promise.reject(new Error('Not authenticated'));
    }

    let fullPath = API_URL + path;
    return this.axiosApi
      .post(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  async postAsync(path, payload) {
    if (!this.axiosApi) {
      throw new Error('Not authenticated');
    }

    let fullPath = API_URL + path;

    try {
      const response = await this.axiosApi.post(fullPath, payload);
      return response;
    } catch (error) {
      console.error('Error on POST request:', path, error);
      throw error; // Re-throw the error for the caller to handle
    }
  }

  login(path, payload, cb) {
    let fullPath = API_URL + path;
    return this.axiosApi
      .post(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  update(path, id, payload, cb) {
    let fullPath = API_URL + path + '/' + id;
    return this.axiosApi
      .patch(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  async updateAsync(path, id, payload) {
    let fullPath = API_URL + path + '/' + id;

    try {
      const response = await this.axiosApi.patch(fullPath, payload);
      return response;
    } catch (error) {
      console.error('Error on POST request:', path, error);
      throw error; // Re-throw the error for the caller to handle
    }
  }

  updateMany(path, payload, filter, cb) {
    let fullPath = API_URL + path + '/update';
    if (filter != null && Object.keys(filter).length > 0) {
      fullPath = fullPath + '?where=' + encodeLoopbackQueryValue(filter);
    }

    return this.axiosApi
      .post(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  upsert(path, payload, cb) {
    let fullPath = API_URL + path;
    return this.axiosApi
      .put(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  upsertWhere(path, payload, filter, cb) {
    let fullPath = API_URL + path + '/upsertWithWhere';
    if (filter != null && Object.keys(filter).length > 0) {
      fullPath = fullPath + '?where=' + encodeLoopbackQueryValue(filter);
    }

    return this.axiosApi
      .post(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  postRequestURL(url, cb) {
    return this.axiosApi
      .get(url)
      .then((response) => cb(response.status, response.data))
      .catch((error) => {
        console.error('Error on request API:', url, error);
      });
  }

  registerStream(model) {
    var urlToChangeStream = `${API_URL}${model}/change-stream?_format=event-stream&access_token=${UserProfile.getToken()}`;
    var src = new EventSource(urlToChangeStream);
    src.addEventListener('data', function (msg) {
      var data = JSON.parse(msg.data);
      console.log(data); // the change object
    });
  }

  delete(path, id, cb) {
    let fullPath = API_URL + path + '/' + id;
    let payload = {
      deleted: true,
      date_deleted: new Date()
    };
    return this.axiosApi
      .patch(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }

  async deleteAsync(path, id) {
    let fullPath = API_URL + path + '/' + id;
    let payload = {
      deleted: true,
      date_deleted: new Date()
    };
    try {
      const response = await this.axiosApi.patch(fullPath, payload);
      return response;
    } catch (error) {
      console.error('Error on DELETE request:', path, error);
      throw error; // Re-throw the error for the caller to handle
    }
  }

  deleteMany(path, filter, cb) {
    let fullPath = API_URL + path + '/update';
    let payload = {
      deleted: true,
      date_deleted: new Date()
    };
    if (filter != null && Object.keys(filter).length > 0) {
      fullPath = fullPath + '?where=' + encodeLoopbackQueryValue(filter);
    }

    return this.axiosApi
      .post(fullPath, payload)
      .then((response) => cb(response.status, response.data));
  }
}

export default Api;
